import { Injectable, Provider, Signal } from '@angular/core';
import { PaginatedResponse } from '../../shared/pagination/paginated-response';
import { buildPaginationQuery } from '../../shared/pagination/pagination.builder';
import { ApiCaseGroup } from '../api-types/case-group.api';
import { ApiCategoryIndustry } from '../api-types/category-industries.api';
import { ApiCategoryValue } from '../api-types/category-value.api';
import { ApiCategoryTask } from '../api-types/category-task.api';
import { ApiContactNatureType } from '../api-types/contact-nature-type.api';
import { ApiContactPointCategory } from '../api-types/contact-point.api';
import { ApiContactType } from '../api-types/contact-type.api';
import { ApiCounty } from '../api-types/county.api';
import { ApiGetLekCategories } from '../api-types/lek.api';
import { ApiPersonType } from '../api-types/person-type.api';
import { ApiPhase } from '../api-types/phase.api';
import { ApiTypeOfCare } from '../api-types/type-of-care.api';
import { CaseGroup } from '../app-types/case-group';
import { CategoryIndustry } from '../app-types/category-industries.app';
import { ContactNatureType } from '../app-types/contact-nature-type';
import { ContactPointCategory } from '../app-types/contact-point.app';
import { ContactType } from '../app-types/contact-type';
import { County } from '../app-types/county.app';
import { PersonType } from '../app-types/person-type.app';
import { Phase } from '../app-types/phase.app';
import { TypeOfCare } from '../app-types/type-of-care';
import { CATEGORIES_API } from '../injection-tokens/services.token';
import { transformApiCaseGroupsToAppCaseGroups } from '../transformers/case-group.transform';
import { transformApiCategoryValuesToAppStrings } from '../transformers/category-value.transform';
import { transformApiIndustriesToAppIndustries } from '../transformers/category-industries.transformer';
import { transformApiCategoryTasksToAppCategoryTasks } from '../transformers/category-task.transform';
import { transformApiContactNatureTypesToAppContactNatureTypes } from '../transformers/contact-nature-type.transform';
import { transformApiCategoriesContactPointToCategoryContactPoints } from '../transformers/contact-point.transform';
import { transformsApiContactTypesToAppContactTypes } from '../transformers/contact-type.transform';
import { transformApiCountiesToAppCounties } from '../transformers/county.transform';
import { transformApiPersonTypesToAppPersonTypes } from '../transformers/person-type.transformer';
import { transformApiPhasesToAppPhases } from '../transformers/phase.transform';
import { transformApiTypesOfCareToAppTypesOfCare } from '../transformers/type-of-care.transform';
import { BaseRequestService } from './base-request.service';
import { environment } from '../../../environments/environment';

@Injectable()
export class CategoriesApi extends BaseRequestService implements CategoriesApiProvider {
	private readonly defaultOffset = environment.pagination.offset;
	private readonly defaultRange = environment.pagination.range;

	public getAllCategoryAssistance = async (assignmentId: number, offset?: number, range?: number) => {
		const apiResponse = await this.get<ApiGetLekCategories>(
			`categories/categoryAssistance/Assignment/${assignmentId}`,
			{
				offset: offset ?? this.defaultOffset,
				range: range ?? this.defaultRange,
			},
		);
		return apiResponse;
	};

	public getCategoryAssistanceOptions = async (offset: number, range: number): Promise<string[]> => {
		const apiResponse = await this.get<PaginatedResponse<ApiCategoryValue>>(
			'categories/categoryAssistance',
			buildPaginationQuery(offset, range),
		);
		return transformApiCategoryValuesToAppStrings(apiResponse.entities);
	};

	public getSponsorOptions = async (offset: number, range: number): Promise<string[]> => {
		const apiResponse = await this.get<PaginatedResponse<ApiCategoryValue>>(
			'categories/sponsors',
			buildPaginationQuery(offset, range),
		);
		return transformApiCategoryValuesToAppStrings(apiResponse.entities);
	};

	/**
	 * Fetch all Phases from the API.
	 * The API data is transformed to the application-specific
	 * format before being returned.
	 * @returns An array of Phases.
	 */
	public getAllPhases = async (offset: number, range: number) => {
		const apiPhases = await this.get<PaginatedResponse<ApiPhase>>(
			'categories/phases',
			buildPaginationQuery(offset, range),
		);
		const phases = transformApiPhasesToAppPhases(apiPhases.entities);
		const totalPhases = apiPhases.totalEntities;
		return {
			phases: phases,
			totalPhases: totalPhases,
		};
	};

	/**
	 * Fetch all Contact Types from the API.
	 * The API data is transformed to the application-specific
	 * format before being returned.
	 * @returns An array of Contact Types.
	 */
	public getAllContactTypes = async (offset: number, range: number) => {
		const apiContactTypes = await this.get<PaginatedResponse<ApiContactType>>(
			'categories/contactType',
			buildPaginationQuery(offset, range),
		);
		const contactTypes = transformsApiContactTypesToAppContactTypes(apiContactTypes.entities);
		const totalContactTypes = apiContactTypes.totalEntities;
		return {
			contactTypes: contactTypes,
			totalContactTypes: totalContactTypes,
		};
	};

	/**
	 * Fetch all Contact Nature Types from the API.
	 * The API data is transformed to the application-specific
	 * format before being returned.
	 * @returns An array of Contact Nature Types.
	 */
	public getAllContactNatureTypes = async (offset: number, range: number) => {
		const apiContactNatureTypes = await this.get<PaginatedResponse<ApiContactNatureType>>(
			'categories/contactNatureType',
			buildPaginationQuery(offset, range),
		);
		const contactNatureTypes = transformApiContactNatureTypesToAppContactNatureTypes(apiContactNatureTypes.entities);
		const totalContactNatureTypes = apiContactNatureTypes.totalEntities;
		return {
			contactNatureTypes: contactNatureTypes,
			totalContactNatureTypes: totalContactNatureTypes,
		};
	};

	public getAllContactPointTypes = async (
		offset?: number,
		range?: number,
	): Promise<PaginatedResponse<ContactPointCategory>> => {
		const response = await this.get<PaginatedResponse<ApiContactPointCategory>>('categories/categoryContactPoints', {
			offset,
			range,
		});
		const contactPointTypes = transformApiCategoriesContactPointToCategoryContactPoints(response.entities);
		return {
			entities: contactPointTypes,
			totalEntities: response.totalEntities,
			offset: response.offset,
			range: response.range,
		};
	};

	/**
	 * Fetch all TypeOfCare from the API.
	 * The API data is transformed to the application-specific
	 * format before being returned.
	 * @returns An array of TypeOfCare.
	 */
	public getAllTypesOfCare = async (
		offset: number,
		range: number,
		serviceOfficeStatement: boolean,
		additionalAssignment: boolean,
		mainAssignment: boolean,
		mainAssignmentId?: number,
	) => {
		const apiTypesOfCare = await this.get<PaginatedResponse<ApiTypeOfCare>>('categories/typeOfCare', {
			offset,
			range,
			serviceOfficeStatement,
			additionalAssignment,
			mainAssignment,
			mainAssignmentId,
		});

		const typesOfCare = transformApiTypesOfCareToAppTypesOfCare(apiTypesOfCare.entities);
		const totalTypesOfCare = apiTypesOfCare.totalEntities;
		return {
			typesOfCare: typesOfCare,
			totalTypesOfCare: totalTypesOfCare,
		};
	};

	/**
	 * Fetch all CaseGroup from the API.
	 * The API data is transformed to the application-specific
	 * format before being returned.
	 * @returns An array of CaseGroup.
	 */
	public getAllCaseGroups = async (offset: number, range: number) => {
		const apiCaseGroups = await this.get<PaginatedResponse<ApiCaseGroup>>(
			'categories/caseGroups',
			buildPaginationQuery(offset, range),
		);
		const caseGroups = transformApiCaseGroupsToAppCaseGroups(apiCaseGroups.entities);
		const totalCaseGroups = apiCaseGroups.totalEntities;
		return {
			caseGroups: caseGroups,
			totalCaseGroups: totalCaseGroups,
		};
	};

	/**
	 * Fetch all PersonType from the API.
	 * The API data is transformed to the application-specific
	 * format before being returned.
	 * @returns An array of PersonType.
	 *
	 */
	public getAllPersonTypes = async (offset?: number, range?: number, searchTerm?: string, personTypeId?: number) => {
		const records: Record<string, unknown> = {};
		records['offset'] = offset;
		records['range'] = range;
		records['searchTerm'] = searchTerm?.trim() ? searchTerm.trim() : undefined;
		records['personTypeId'] = personTypeId;
		const apiResponse = await this.get<PaginatedResponse<ApiPersonType>>('categories/categoryPersonTypes', records);

		const personTypes = transformApiPersonTypesToAppPersonTypes(apiResponse.entities);
		const response: PaginatedResponse<PersonType> = {
			entities: personTypes,
			totalEntities: apiResponse.totalEntities,
			offset: apiResponse.offset,
			range: apiResponse.range,
		};
		return response;
	};

	/**
	 * Fetch all Counties from the API.
	 * The API data is transformed to the application-specific
	 * format before being returned.
	 * @returns An array of County.
	 */
	public getAllCounties = async (offset: number, range: number, searchTerm?: string) => {
		const apiResponse = await this.get<PaginatedResponse<ApiCounty>>(
			'categories/categoryCounties',
			buildPaginationQuery(offset, range, searchTerm?.trim() ? searchTerm.trim() : undefined),
		);

		const counties = transformApiCountiesToAppCounties(apiResponse.entities);
		return {
			...apiResponse,
			entities: counties,
		};
	};

	/**
	 * Fetch all Industries from the API.
	 * The API data is transformed to the application-specific
	 * format before being returned.
	 * @returns An array of CategoryIndustry.
	 */
	public getAllIndustries = async (offset: number, range: number, searchTerm?: string) => {
		const apiResponse = await this.get<PaginatedResponse<ApiCategoryIndustry>>(
			'categories/categoryIndustries',
			buildPaginationQuery(offset, range, searchTerm?.trim() ? searchTerm.trim() : undefined),
		);

		const industries = transformApiIndustriesToAppIndustries(apiResponse.entities);
		return {
			...apiResponse,
			entities: industries,
		};
	};

	/**
	 * Fetch all task categories from the API.
	 * @param offset
	 * @param range
	 * @param phaseId The Phase Id if the assignment.
	 * @param typeOfCareId the Type Of Care Id in the assignment.
	 * @returns a Paginated Response of CategoryTask
	 */
	public getAllCategoryTasks = async (offset?: number, range?: number, phaseId?: number, typeOfCareId?: number) => {
		const response = await this.get<PaginatedResponse<ApiCategoryTask>>('categories/categoryTasks', {
			offset: offset,
			range: range,
			phaseId: phaseId,
			typeOfCareId: typeOfCareId,
		});

		const tasks = transformApiCategoryTasksToAppCategoryTasks(response.entities);

		return {
			entities: tasks,
			totalEntities: response.totalEntities,
			offset: response.offset,
			range: response.range,
		};
	};
}

/**
 * Interface for Categories API.
 */
export interface CategoriesApiProvider extends BaseRequestService {
	isLoading: Signal<boolean>;
	getAllPhases: (offset: number, range: number) => Promise<{ phases: Phase[]; totalPhases: number }>;
	getAllContactTypes: (
		offset: number,
		range: number,
	) => Promise<{ contactTypes: ContactType[]; totalContactTypes: number }>;
	getAllContactNatureTypes: (
		offset: number,
		range: number,
	) => Promise<{ contactNatureTypes: ContactNatureType[]; totalContactNatureTypes: number }>;
	getAllTypesOfCare: (
		offset: number,
		range: number,
		serviceOfficeStatement: boolean,
		additionalAssignment: boolean,
		mainAssignment: boolean,
		mainAssignmentId?: number,
	) => Promise<{ typesOfCare: TypeOfCare[]; totalTypesOfCare: number }>;
	getAllCaseGroups: (offset: number, range: number) => Promise<{ caseGroups: CaseGroup[]; totalCaseGroups: number }>;
	getAllPersonTypes: (
		offset?: number,
		range?: number,
		searchTerm?: string,
		personTypeId?: number,
	) => Promise<PaginatedResponse<PersonType>>;
	getAllContactPointTypes: (offset?: number, range?: number) => Promise<PaginatedResponse<ContactPointCategory>>;
	getAllCounties: (offset: number, range: number, searchTerm?: string) => Promise<PaginatedResponse<County>>;
	getAllIndustries: (
		offset: number,
		range: number,
		searchTerm?: string,
	) => Promise<PaginatedResponse<CategoryIndustry>>;
	getAllCategoryAssistance: (assignmentId: number) => Promise<ApiGetLekCategories>;
	getCategoryAssistanceOptions: (offset: number, range: number) => Promise<string[]>;
	getSponsorOptions: (offset: number, range: number) => Promise<string[]>;
	getAllCategoryTasks: (
		offset?: number,
		range?: number,
		phaseId?: number,
		typeOfCareId?: number,
	) => Promise<PaginatedResponse<ApiCategoryTask>>;
}

@Injectable()
export class CategoriesMockService extends BaseRequestService {
	public constructor() {
		super();
		console.warn('⛔️⛔️⛔️ Categories Mock Service: You are currently using a mock service!');
	}

	private readonly typesOfCare: TypeOfCare[] = [
		{
			id: 1,
			initial: 'VM',
			integrationOfficeId: 1,
			remark: 'Vermittlung',
		},
		{
			id: 2,
			initial: 'SG',
			integrationOfficeId: 1,
			remark: 'Sicherung',
		},
	];

	private readonly caseGroups: CaseGroup[] = [
		{
			id: 1,
			description: 'Keine Fallgruppe',
		},
		{
			id: 2,
			description: 'Wesentliche Behinderung',
		},
		{
			id: 3,
			description: 'Sonderpäd. Fb Lernen',
		},
	];

	private readonly personTypes: PersonType[] = [
		{
			id: 1,
			name: 'Angehoerige',
			isInitiator: true,
		},
		{
			id: 2,
			name: 'Klient',
			isInitiator: true,
		},
		{
			id: 3,
			name: 'Berater',
			isInitiator: true,
		},
	];

	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	public getAllTypesOfCare = async (offset: number, range: number) => {
		return { typesOfCare: this.typesOfCare };
	};

	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	public getAllCaseGroups = async (offset: number, range: number) => {
		return { caseGroups: this.caseGroups };
	};

	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	public getAllPersonTypes = async (offset?: number, range?: number, searchTerm?: string, personTypeId?: number) => {
		return { entities: this.personTypes };
	};
}

export const provideCategoryApi: Provider = {
	provide: CATEGORIES_API,
	useClass: CategoriesApi,
};
